// Complete updated ResetPasswordScreen component
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthLayout from 'src/components/Auth/AuthLayout';
import modalStyle from '../../../components/Modal/ConfirmationModal.module.css'
import SuccessCard from 'src/components/Card/SuccessCard';

const ResetPasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [successMessage, setSuccessMessage] = useState("")

  const username = location.state?.email || '';
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(''); // Clear previous errors

    const resetPasswordDto = {
      username,
      NewPassword: newPassword,
      ConfirmPassword: confirmPassword,
      OTP: otp,
    };

    try {
      const response = await axios.post(`${baseUrl}/account/resetPassword`, resetPasswordDto, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = response.data;

      if (response.status === 200 && data.flag) {
        setSuccessMessage('You have successfully reset your password.');
      } else {
        // Handle specific error messages from backend
        setError(data.details || data.message || 'Failed to reset password. Please try again.');
      }
    } catch (error) {
      // Enhanced error handling based on controller responses
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          // Handle specific BadRequest errors
          if (data.error && data.error.includes("OTP Expired")) {
            setError("Your OTP has expired. Please request a new one.");
          } else if (data.error && (data.error.includes("Invalid OTP") || data.error.includes("OTP Validation Failed"))) {
            setError("The OTP you entered is incorrect. Please try again.");
          } else if (data.error && data.error.includes("OTP Not Found")) {
            setError("OTP not found. Please request a new OTP.");
          } else if (data.error && data.error.includes("Password reset failed")) {
            setError("Password reset failed. Please check your input and try again.");
          } else if (data.error && data.error.includes("Invalid request")) {
            setError(data.details || "Invalid request. Please check all fields and try again.");
          } else {
            setError(data.details || data.error || "Failed to reset password. Please try again.");
          }
        } else if (status === 404) {
          // Handle NotFound errors
          if (data.error && data.error.includes("User not found")) {
            setError("User account not found. Please check your email and try again.");
          } else {
            setError("Account not found. Please contact support.");
          }
        } else if (status === 500) {
          // Handle Internal Server Error
          setError("Server error occurred. Please try again later or contact support.");
        } else {
          // Handle other HTTP status codes
          setError(data.details || data.error || "An unexpected error occurred. Please try again.");
        }
      } else if (error.request) {
        // Network error
        setError("Network error. Please check your connection and try again.");
      } else {
        // Other errors
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    setResendLoading(true);
    setError(''); // Clear previous errors
    try {
      await axios.get(`${baseUrl}/account/requestOtp?email=${username}`);
      setCooldown(60); 
      setError(''); // Clear any previous errors on successful resend
    } catch (error) {
      // Enhanced error handling for resend OTP
      if (error.response && error.response.data) {
        setError(error.response.data.details || error.response.data.error || "Failed to resend OTP. Please try again.");
      } else {
        setError("Error resending OTP. Please try again.");
      }
    } finally {
      setResendLoading(false);
    }
  };

  const validate = () => {
    if (!newPassword || !confirmPassword || !otp || loading) {
      return true
    }
    else if (newPassword !== confirmPassword) {
      return true
    }
    else {
      return false
    }
  }

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  useEffect(() => {
    const Authorized = localStorage.getItem('authenticated')
    const hasToken = sessionStorage.getItem('token')
    if (Authorized && hasToken) {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate]);

  return (
    <main className={`bg-white w-100`}>
      <div className='d-flex w-100 p-0'>
        <div className='w-50'>
            <AuthLayout/>
          </div>
          <div className="col-12 col-md-6 col-lg-6">
            <div className='d-flex flex-column justify-content-center align-items-center min-vh-100'>
              {!successMessage ? 
              (<form onSubmit={(e) => handleSubmit(e)} className='my-auto col-10 col-md-6'>
                <div>
                  <h6 className='fw-medium fs-3'>Reset Password</h6>
                  <p className="text-medium-emphasis">Input the OTP that was sent to your email address and create a new password</p>
                </div>

                <div className="mb-3 d-flex flex-column">
                <label className='fw-medium text-start'>New Password</label>
                  <input
                    className={`${modalStyle.modalButton} ${modalStyle.modal_password}`}
                    type="password"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3 d-flex flex-column">
                  <label className='fw-medium text-start'>Confirm Password</label>
                  <input
                    className={`${modalStyle.modalButton} ${modalStyle.modal_password}`}
                    type="password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    onBlur={() => (newPassword !== confirmPassword) ? setError('Passwords do not match') : setError("")}
                  />
                </div>

                <div className="mb-3 d-flex flex-column">
                  <label className='fw-medium text-start'>OTP</label>
                  <input
                    type='number'
                    className={`${modalStyle.modalButton} ${modalStyle.modal_password}`}
                    placeholder="OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    maxLength={6}
                    onInput={(e) => e.target.value = e.target.value.slice(0, 6)}
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    className={`${modalStyle.modalButton} ${ validate() ? modalStyle.modal_password : modalStyle.modal_confirm} px-4 ${validate()? '' : 'text-white'} mb-4 fw-medium`}
                    disabled={validate()}
                  >
                    {loading ? 'Loading...' : 'Reset Password'}
                  </button>

                  <button
                    type='button'
                    className={`${modalStyle.modalButton} px-4 fw-medium fs-5`}
                    onClick={resendOTP}
                    style={{ color: '#521c78', backgroundColor: 'transparent'}}
                    disabled={resendLoading || cooldown > 0}
                  >
                    {resendLoading ? 'Resending...' : cooldown > 0 ? `Resend OTP (${cooldown})` : 'Resend OTP'}
                  </button>
                </div>
                
                {error && <p className="text-danger mt-2">{error}</p>}
              </form>) : 
              (
                <SuccessCard
                  confirm={() => navigate('/login')}
                  successMessage={successMessage}
                />
              )}
            </div>
        </div>
      </div>
    </main>
  );
};

export default ResetPasswordScreen;